/**
 * 服务端时间校准工具
 *
 * 背景：倒计时原本依赖客户端本地时间（Date.now / new Date），
 * 用户系统时间存在误差时会导致倒计时不准。后端提供
 * `/charge/user/serverTime` 接口返回服务端 epoch 毫秒，
 * 前端据此计算与本地时钟的偏移量（offset），后续统一用
 * getServerNow() 获取「校准后的当前时间」参与倒计时计算。
 *
 * 说明：offset = 服务端时间 - 本地时间。为消除接口网络时延带来的
 * 误差，校准时记录「发起请求时刻 t0」与「收到回复时刻 t1」，用
 * 往返耗时的一半 (t1 - t0) / 2 估算「服务器 → 用户端」的单程时延，
 * 将其叠加到服务端时间上，得到回复到达客户端瞬间对应的服务端时间：
 *   offset = (serverTime + (t1 - t0) / 2) - t1
 * getServerNow() = Date.now() + offset，即用本地时钟的流逝量
 * 叠加一次性测得的偏移，既能纠正系统时间误差，又不受
 * setInterval 在后台被节流的影响（Date.now 始终反映真实流逝）。
 */

import unionAxios from "@/utils/union-axios";

/** 服务端时间与本地时间的偏移量（毫秒），未校准时为 0 */
let serverTimeOffset = 0;
/** 是否已成功校准过 */
let synced = false;
/** 进行中的校准请求，避免同一时刻重复请求 */
let pendingSync: Promise<number> | null = null;
/** 最近一次成功校准的本地时间 */
let lastSyncedAt = 0;
/** 普通校准请求的缓存有效期，避免页面内不同模块错峰挂载时重复请求 */
const SYNC_CACHE_TTL = 30 * 1000;
const listeners = new Set<() => void>();
const clockListeners = new Set<() => void>();

export interface ServerClockSnapshot {
  serverNow: number;
  monotonicNow: number;
}

const getMonotonicNow = (): number =>
  typeof performance === "undefined" ? Date.now() : performance.now();

let clockSnapshot: ServerClockSnapshot = {
  serverNow: Date.now(),
  monotonicNow: getMonotonicNow(),
};
let clockTimer: ReturnType<typeof setTimeout> | null = null;

function publishClock(): void {
  clockSnapshot = {
    serverNow: getServerNow(),
    monotonicNow: getMonotonicNow(),
  };
  clockListeners.forEach((listener) => listener());
}

function scheduleClockTick(): void {
  if (clockTimer) clearTimeout(clockTimer);
  if (clockListeners.size === 0) {
    clockTimer = null;
    return;
  }

  const remainder = ((getServerNow() % 1000) + 1000) % 1000;
  const delay = remainder === 0 ? 1000 : 1000 - remainder;
  clockTimer = setTimeout(() => {
    publishClock();
    scheduleClockTick();
  }, delay);
}

interface ServerTimeResp {
  code: number;
  data?: {
    serverTime?: number;
  };
  msg?: string;
}

/**
 * 请求服务端时间
 */
export function fetchServerTime(): Promise<ServerTimeResp> {
  return unionAxios.post(
    `/web-h5/charge/user/serverTime`,
  ) as Promise<ServerTimeResp>;
}

/**
 * 校准服务端时间，更新本地偏移量。
 * 失败时静默降级为本地时间（offset 保持不变），不阻断业务。
 * @returns 最新的 offset（毫秒）
 */
export function syncServerTime(force = false): Promise<number> {
  if (pendingSync) return pendingSync;
  if (!force && synced && Date.now() - lastSyncedAt < SYNC_CACHE_TTL) {
    return Promise.resolve(serverTimeOffset);
  }

  // 发起请求的时刻，用于估算网络往返时延
  const requestStart = Date.now();

  pendingSync = fetchServerTime()
    .then((res): number => {
      // 收到回复的时刻
      const responseEnd = Date.now();
      const serverTime = Number(res?.data?.serverTime);
      if (serverTime && !Number.isNaN(serverTime)) {
        // 往返耗时的一半视为「服务器 → 用户端」的单程时延，
        // 补偿到服务端时间上，得到回复到达客户端瞬间的服务端时间
        const oneWayLatency = (responseEnd - requestStart) / 2;
        serverTimeOffset = serverTime + oneWayLatency - responseEnd;
        synced = true;
        lastSyncedAt = responseEnd;
        listeners.forEach((listener) => listener());
        publishClock();
        scheduleClockTick();
      }
      return serverTimeOffset;
    })
    .catch((err): number => {
      console.warn("[serverTime] 校准失败，降级为本地时间", err);
      return serverTimeOffset;
    })
    .finally((): void => {
      pendingSync = null;
    });

  return pendingSync;
}

/**
 * 获取校准后的当前时间（epoch 毫秒）。
 * 未校准成功时退回本地 Date.now()。
 */
export function getServerNow(): number {
  return Date.now() + serverTimeOffset;
}

/** 统一倒计时取整规则：不足一秒按一秒展示 */
export function getRemainingSeconds(endTime: number, now: number): number {
  return Math.max(0, Math.ceil((endTime - now) / 1000));
}

/** 当前的服务端时间偏移量（毫秒） */
export function getServerTimeOffset(): number {
  return serverTimeOffset;
}

/** 订阅服务端时间偏移变化，供所有倒计时同步刷新 */
export function subscribeServerTime(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 获取由全局 ticker 发布的稳定时间快照 */
export function getServerClockSnapshot(): ServerClockSnapshot {
  return clockSnapshot;
}

/** 订阅全局时钟；所有消费者共享同一整秒刷新节拍 */
export function subscribeServerClock(listener: () => void): () => void {
  if (clockListeners.size === 0) {
    publishClock();
  }
  clockListeners.add(listener);
  scheduleClockTick();

  return () => {
    clockListeners.delete(listener);
    if (clockListeners.size === 0 && clockTimer) {
      clearTimeout(clockTimer);
      clockTimer = null;
    }
  };
}

/** 是否已成功完成过服务端时间校准 */
export function isServerTimeSynced(): boolean {
  return synced;
}
